/**
 * A small, capped list of listing keys kept in this browser's localStorage.
 * Backs the signed-out "Hide this home" (components/search/hidden-device.ts)
 * and the signed-out listing Save (lib/saved-device.ts), FLEET_FORWARD W12.
 *
 * Every read and write is wrapped: private mode, a full quota, or a blocked
 * storage API fails open (an empty list, a false return, nothing thrown).
 * Call from effects and event handlers only, never during render
 * (ci:hydration-safety).
 */

export type DeviceKeyList = {
  storageKey: string
  max: number
  /** Keys on this device, newest first. Empty when storage is unavailable. */
  read: () => string[]
  has: (key: string) => boolean
  /** True when stored. Re-adding moves the key to the front. */
  add: (key: string) => boolean
  /** A key that was never stored is a success no-op. */
  remove: (key: string) => void
}

function normalize(key: unknown): string {
  return typeof key === 'string' || typeof key === 'number' ? String(key).trim() : ''
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export function deviceKeyList(storageKey: string, max: number): DeviceKeyList {
  function read(): string[] {
    const store = storage()
    if (!store) return []
    try {
      const parsed: unknown = JSON.parse(store.getItem(storageKey) ?? '[]')
      if (!Array.isArray(parsed)) return []
      const out: string[] = []
      for (const raw of parsed) {
        const key = normalize(raw)
        if (key && !out.includes(key)) out.push(key)
        if (out.length >= max) break
      }
      return out
    } catch {
      return []
    }
  }

  function write(keys: string[]): boolean {
    const store = storage()
    if (!store) return false
    try {
      store.setItem(storageKey, JSON.stringify(keys.slice(0, max)))
      return true
    } catch {
      return false
    }
  }

  return {
    storageKey,
    max,
    read,
    has: (key) => {
      const k = normalize(key)
      return k !== '' && read().includes(k)
    },
    add: (key) => {
      const k = normalize(key)
      if (!k) return false
      return write([k, ...read().filter((x) => x !== k)])
    },
    remove: (key) => {
      const k = normalize(key)
      if (!k) return
      const current = read()
      if (current.includes(k)) write(current.filter((x) => x !== k))
    },
  }
}
